import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DropdownButton from './DropdownButton'
import checkboxDash from '../../assets/checkboxDash.svg'
import Popout from '../Popout'
import BadgeSVG from '../BadgeSVG/BadgeSVG'
import { Emoji } from 'src/app/types'
import { getBadgeNameFromEmoji } from 'src/utils/helpers'

type FilterDropdownProps = {
  name: string
  items: string[]
  setSelectedOptions: (items: string[]) => void
  selectedValues: string[]
  selectedText: { singularText: string; pluralText: string }
  isBadge?: boolean
    isTags?: boolean
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  items,
  selectedValues,
  setSelectedOptions,
  name,
  selectedText: { singularText, pluralText },
  isBadge = false,
    isTags = false
}) => {
  const [isMainCheckboxChecked, setIsMainCheckboxChecked] = useState(false)
  const [isMainCheckboxIntermediate, setIsMainCheckboxIntermediate] =
    useState(false)

  useEffect(() => {
    if (selectedValues.length === items.length) {
      setIsMainCheckboxChecked(true)
    }
  }, [selectedValues.length, items.length])

  const getAdjustedSelectedText = useMemo(() => {
    let adjustedName = name
    if (selectedValues.length !== items.length && !isTags) {
      const text = selectedValues.length === 1 ? singularText : pluralText
      adjustedName = `${text} (${selectedValues.length})`
    } else if (selectedValues.length === 0 && items.length !== 0 && !isTags) {
      adjustedName = `${singularText} (0)`
    }else if(isTags) {
        adjustedName = `Filter by Tags`
    }
    return adjustedName
  }, [name, pluralText, selectedValues.length, singularText, items.length, isTags])

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let newSelectedValues = [...selectedValues]
      const option = event.target.value
      if (event.target.checked) {
        newSelectedValues.push(option)
      } else {
        newSelectedValues = newSelectedValues.filter((item) => item !== option)
      }
      /** Tansition the "all" checkbox */
      // uncheck checkbox if none of the options are selected.
      if (newSelectedValues.length === 0) {
        setIsMainCheckboxChecked(false)
        setIsMainCheckboxIntermediate(false)
      }
      // check if all options are selected
      else if (newSelectedValues.length === items.length) {
        setIsMainCheckboxChecked(true)
        setIsMainCheckboxIntermediate(false)
      }
      // else, assign intermediate state
      else {
        setIsMainCheckboxIntermediate(true)
        setIsMainCheckboxChecked(false)
      }
      setSelectedOptions(newSelectedValues)
    },
    [items, selectedValues, setSelectedOptions]
  )

  const handleAllCheckboxes = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setIsMainCheckboxChecked(true)
        setSelectedOptions(items)
      } else {
        setIsMainCheckboxChecked(false)
        setSelectedOptions([])
      }
      setIsMainCheckboxIntermediate(false)
    },
    [items, setSelectedOptions]
  )

  return (
    <Popout
      contents={
        <div className="w-[250px] overflow-auto max-h-[150px] scrollbar-hide">
          <div className="flex items-center py-2 px-2 border-b">
            <label className="cursor-pointer relative" htmlFor="all">
              <input
                id="all"
                type="checkbox"
                className={`${
                  isMainCheckboxIntermediate
                    ? 'appearance-none bg-primaryBlue'
                    : ''
                } w-4 h-4 flex items-center flex-shrink-0 rounded-md cursor-pointer`}
                onChange={handleAllCheckboxes}
                checked={isMainCheckboxChecked}
              />
              {isMainCheckboxIntermediate && (
                <img
                  className="absolute top-1"
                  src={checkboxDash}
                  alt="intermediate"
                />
              )}
            </label>

            <label className="ml-3 text-sm font-medium">{name}</label>
          </div>

          {items.map((item) => (
            <div
              key={item}
              className="flex items-center rounded-md p-1.5 ml-5 hover:bg-dropdownItemsHoverBlue"
            >
              <input
                id={item}
                type="checkbox"
                className="w-4 h-4 flex-shrink-0 rounded-md cursor-pointer"
                checked={selectedValues?.includes(item)}
                onChange={handleCheckboxChange}
                value={item}
              />
              <label className="ml-3 text-sm font-medium">
                {isBadge ? (
                  <BadgeSVG name={getBadgeNameFromEmoji(item as Emoji)} />
                ) : (
                  item
                )}
              </label>
            </div>
          ))}
        </div>
      }
      popoutClass="rounded-lg bg-white shadow-tooltip border-borderGrey border-[0.5px] px-2 py-2 z-30"
      clickEnabled={true}
      hoverEnabled={false}
      ignoreMouse={false}
      placement={'bottom-start'}
    >
      {() => {
        return (
          <DropdownButton
            children={getAdjustedSelectedText}
            ariaLabel="filter-dropdown"
            type={
              selectedValues.length !== items.length && !isTags ? 'filterSelect' : 'filter'
            }
            styles={'flex items-center'}
          />
        )
      }}
    </Popout>
  )
}

export default FilterDropdown
